import { ReactNode, useState } from "react";
import {
	JobEvent,
	outcomeLabels,
	ArchiveOutcome,
	xlate
} from "../../../DataModels/DataModels";
import { formatEventTimestamp } from "../../../Utilities/FormatEventTimestamp";
import {
	Sparkle,
	Mail,
	Pencil,
	CheckCircle,
	Person,
	ArrowForward
} from "../Icons/HistoryIcons";

interface HistoryEventProps {
	event: JobEvent;
	isLast: boolean;
}

function renderEventContent(event: JobEvent): {
	headline: string;
	detail: string | null;
	icon: ReactNode;
	iconColor: string;
	expandable: boolean;
	expandContent?: string;
} {
	switch (event.eventType) {
		case "job.create":
			return {
				headline: "Job Created",
				detail: `Applied to ${event.eventData.title} at ${event.eventData.company}`,
				icon: ArrowForward(),
				iconColor: "text-primary-active",
				expandable: false
			};
		case "job.change_state":
			return {
				headline: `Moved to ${xlate[event.eventData.toState] ?? event.eventData.toState}`,
				detail: event.eventData.reason ? `"${event.eventData.reason}"` : null,
				icon: ArrowForward(),
				iconColor: "text-primary-active",
				expandable: false
			};
		case "job.note":
			return {
				headline: "Note Added",
				detail: `"${event.eventData.text}"`,
				icon: Pencil(),
				iconColor: "text-primary-inactive",
				expandable: false
			};
		case "job.archive": {
			const outcomeLabel = event.eventData.outcome
				? outcomeLabels[event.eventData.outcome as ArchiveOutcome]
				: null;
			const parts = [
				outcomeLabel ? `Outcome: ${outcomeLabel}` : null,
				event.eventData.reason ? `"${event.eventData.reason}"` : null
			].filter(Boolean);
			return {
				headline: "Archived",
				detail: parts.length ? parts.join("\n") : null,
				icon: CheckCircle(),
				iconColor: "text-primary-inactive",
				expandable: false
			};
		}
		case "resume.generate":
			return {
				headline: "Resume Customized",
				detail: "AI-tailored resume generated for this role",
				icon: Sparkle(),
				iconColor: "text-primary-active",
				expandable: false
			};
		case "cover_letter.generate":
			return {
				headline: "Cover Letter Written",
				detail: "AI-generated cover letter created",
				icon: Sparkle(),
				iconColor: "text-primary-active",
				expandable: false
			};
		case "cover_letter.edit":
			return {
				headline: "Cover Letter Edited",
				detail: "Cover letter was manually updated",
				icon: Sparkle(),
				iconColor: "text-primary-active",
				expandable: false
			};
		case "contact.add":
			return {
				headline: "Contact Added",
				detail: `${event.eventData.contactName}${event.eventData.contactRole ? ` · ${event.eventData.contactRole}` : ""}`,
				icon: Person(),
				iconColor: "text-primary",
				expandable: false
			};
		case "contact.remove":
			return {
				headline: "Contact Removed",
				detail: `${event.eventData.contactName}${event.eventData.contactRole ? ` · ${event.eventData.contactRole}` : ""}`,
				icon: Person(),
				iconColor: "text-primary-inactive",
				expandable: false
			};
		case "thank_you_letter.generate":
			return {
				headline: "Thank You Letter Sent",
				detail: `To ${event.eventData.recipientName}${event.eventData.recipientRole ? ` · ${event.eventData.recipientRole}` : ""}`,
				icon: Mail(),
				iconColor: "text-primary",
				expandable: true,
				expandContent: event.eventData.letterContent
			};
		case "ghosting_letter.generate":
			return {
				headline: "Follow-Up Letter Sent",
				detail: `To ${event.eventData.recipientName}${event.eventData.daysSinceLastContact ? ` · ${event.eventData.daysSinceLastContact} days since last contact` : ""}`,
				icon: Mail(),
				iconColor: "text-primary",
				expandable: true,
				expandContent: event.eventData.letterContent
			};
	}
}

const HistoryEvent: React.FC<HistoryEventProps> = ({ event, isLast }) => {
	const [expanded, setExpanded] = useState(false);
	const { headline, detail, icon, iconColor, expandable, expandContent } =
		renderEventContent(event);

	return (
		<div className="relative flex gap-4">
			{/* Timeline line + icon */}
			<div className="flex flex-col items-center">
				<div className={`w-5 h-5 shrink-0 mt-0.5 ${iconColor}`}>{icon}</div>
				{!isLast && (
					<div className="w-0.5 bg-primary-accent flex-1 min-h-[24px]" />
				)}
			</div>

			{/* Content */}
			<div className="pb-6 flex-1 min-w-0">
				<div className="flex items-start justify-between gap-2">
					<p className="text-primary font-bold text-sm">{headline}</p>
					{expandable && (
						<button
							onClick={() => setExpanded(!expanded)}
							className="text-xs text-primary-inactive hover:text-primary shrink-0"
						>
							{expanded ? "[Hide]" : "[View]"}
						</button>
					)}
				</div>
				{detail && (
					<p className="text-primary text-sm mt-0.5 whitespace-pre-line">
						{detail.startsWith('"') ? (
							<span className="italic">{detail}</span>
						) : (
							detail
						)}
					</p>
				)}
				<p className="text-sm text-primary-inactive mt-1">
					{formatEventTimestamp(event.eventTimestamp)}
				</p>
				{expanded && expandContent && (
					<div className="mt-2 p-3 rounded-lg border border-primary-accent bg-primary-accent/20 text-sm text-primary whitespace-pre-wrap">
						{expandContent}
					</div>
				)}
			</div>
		</div>
	);
};

export default HistoryEvent;
